import { Intent as BlueprintIntent } from '@blueprintjs/core';
import { OverflowMenuHorizontal } from '@carbon/icons-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Menu,
  MenuItem,
  MenuItems,
  StaticAvatar,
} from 'vet-bones/design-system/components';
import { alphaSort } from 'vet-bones/utils';

import { useAppDispatch } from 'src/ui/app/hooks';
import { DataTable, DataTableColumn } from 'src/ui/components/DataTable';
import { withConfirmationDs } from 'src/ui/components/withConfirmationDs';
import {
  ORGANIZATION_MEMBER_TABLE_COLUMN_ACTIONS,
  ORGANIZATION_MEMBER_TABLE_COLUMN_NAME,
  ORGANIZATION_MEMBER_TABLE_COLUMN_NAME_SORT,
  ORGANIZATION_MEMBER_TABLE_COLUMN_ROLE,
  ORGANIZATION_MEMBER_TABLE_COLUMN_ROLE_SORT,
  ORGANIZATION_MEMBER_TABLE_COLUMN_STATUS,
  ORGANIZATION_MEMBER_TABLE_COLUMN_STATUS_SORT,
  ORGANIZATION_MEMBER_TABLE_CONFIRMATION_YES,
  ORGANIZATION_MEMBER_TABLE_ROW_ACTIONS,
  ORGANIZATION_MEMBER_TABLE_ROW_ACTIONS_MENU,
  ORGANIZATION_MEMBER_TABLE_ROW_ACTIONS_MENU_ITEM,
  ORGANIZATION_MEMBER_TABLE_ROW_NAME,
  ORGANIZATION_MEMBER_TABLE_SEARCH,
} from 'src/ui/constants/testIds';
import { queueMessage, queueNotification } from 'src/ui/features/notifications';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  useAddOrganizationInvitationsMutation,
  useDeleteInvitationsMutation,
  useRemoveOrganizationMembersMutation,
} from 'src/ui/graph/types';
import { useProfile } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import {
  OrganizationRole,
  useInvalidateOrganizationMembers,
  useIsOrganizationAdmin,
  useOrganization,
} from 'src/ui/hooks/organization';
import * as copy from 'src/ui/templates/copy';

type MemberStatus = 'active' | 'invited' | 'expired';

interface MemberTableItem {
  initials: string;
  name?: string;
  email: string;
  role: OrganizationRole;
  status: MemberStatus;
}

const MEMBER_COLUMNS: Array<DataTableColumn<MemberTableItem>> = [
  {
    key: 'name',
    className: 'organization-table-row-user-name',
    header: copy.components.organization.memberTable.columns.name,
    sortByAlpha: (item: MemberTableItem) => item.name || item.email,
    sortTestId: ORGANIZATION_MEMBER_TABLE_COLUMN_NAME_SORT,
    testId: ORGANIZATION_MEMBER_TABLE_COLUMN_NAME,
  },
  {
    key: 'role',
    className: 'organization-table-row-member-role',
    header: copy.components.organization.memberTable.columns.role,
    sortByAlpha: (item: MemberTableItem) => item.role,
    sortTestId: ORGANIZATION_MEMBER_TABLE_COLUMN_ROLE_SORT,
    testId: ORGANIZATION_MEMBER_TABLE_COLUMN_ROLE,
  },
];

const ADMIN_COLUMNS: Array<DataTableColumn<MemberTableItem>> = [
  ...MEMBER_COLUMNS,
  {
    key: 'status',
    className: 'data-table-row-status',
    header: copy.components.organization.memberTable.columns.status,
    sortByAlpha: (item: MemberTableItem) => item.status,
    sortTestId: ORGANIZATION_MEMBER_TABLE_COLUMN_STATUS_SORT,
    testId: ORGANIZATION_MEMBER_TABLE_COLUMN_STATUS,
  },
  {
    key: 'actions',
    className: 'data-table-row-actions',
    header: copy.components.organization.memberTable.columns.actions,
    testId: ORGANIZATION_MEMBER_TABLE_COLUMN_ACTIONS,
  },
];

export const OrganizationMembersTable: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org } = useParams<{ org: string }>();
  const onError = useOnError();
  const onMutate = useOnMutate();

  const { data: profile } = useProfile();
  const { membersByEmail, invitationsByEmail } = useOrganization(org);
  const isAdmin = useIsOrganizationAdmin(org);
  const invalidateMembers = useInvalidateOrganizationMembers(org);

  const { mutate: removeMember } = useRemoveOrganizationMembersMutation(
    graphQLClient,
    {
      onMutate,
      onError,
      onSuccess: (data) => {
        invalidateMembers();

        const result = data.removeOrganizationMembers?.results?.[0];
        if (!result?.success) {
          dispatch(
            queueNotification(
              result?.error ||
                copy.components.organization.memberTable.notifications
                  .removeMember.error
            )
          );
          return;
        }

        dispatch(
          queueMessage({
            intent: BlueprintIntent.SUCCESS,
            message:
              copy.components.organization.memberTable.notifications
                .removeMember.success,
          })
        );
      },
    }
  );

  const { mutate: deleteInvitation } = useDeleteInvitationsMutation(
    graphQLClient,
    {
      onMutate,
      onError,
      onSuccess: (data) => {
        invalidateMembers();

        const result = data.deleteInvitations?.results?.[0];
        if (!result?.success) {
          dispatch(
            queueNotification(
              result?.error ||
                copy.components.organization.memberTable.notifications
                  .removeInvitation.error
            )
          );
          return;
        }

        dispatch(
          queueMessage({
            intent: BlueprintIntent.SUCCESS,
            message:
              copy.components.organization.memberTable.notifications
                .removeInvitation.success,
          })
        );
      },
    }
  );

  const { mutate: resendInvitation } = useAddOrganizationInvitationsMutation(
    graphQLClient,
    {
      onMutate,
      onError,
      onSuccess: (data) => {
        invalidateMembers();

        const result = data.addOrganizationInvitations?.results?.[0];
        if (!result?.success) {
          dispatch(
            queueNotification(
              result?.error ||
                copy.components.organization.memberTable.notifications
                  .resendInvitation.error
            )
          );
          return;
        }

        dispatch(
          queueMessage({
            intent: BlueprintIntent.SUCCESS,
            message:
              copy.components.organization.memberTable.notifications
                .resendInvitation.success,
          })
        );
      },
    }
  );

  const memberItems = React.useMemo(() => {
    const items: MemberTableItem[] = [];
    Object.values(membersByEmail).forEach((member) => {
      items.push({
        initials: [member.firstName[0], member.lastName[0]]
          .filter(Boolean)
          .join(''),
        name: [member.firstName, member.lastName].filter(Boolean).join(' '),
        email: member.email,
        role: member.role,
        status: 'active',
      });
    });
    Object.values(invitationsByEmail).forEach((invitation) => {
      if (membersByEmail[invitation.email]) {
        return;
      }

      items.push({
        initials: invitation.email[0],
        email: invitation.email,
        role: invitation.role,
        status: invitation.expired ? 'expired' : 'invited',
      });
    });
    return items.sort(alphaSort((item) => item.name || item.email));
  }, [membersByEmail, invitationsByEmail]);

  const handleRemoveMember = (member: MemberTableItem) => {
    withConfirmationDs({
      title:
        copy.components.organization.memberTable.confirmations.removeMember
          .title,
      message: copy.components.organization.memberTable.confirmations.removeMember.message(
        member.name || member.email
      ),
      yesPrompt:
        copy.components.organization.memberTable.confirmations.removeMember
          .confirm,
      yesPromptTestId: ORGANIZATION_MEMBER_TABLE_CONFIRMATION_YES,
      noPrompt:
        copy.components.organization.memberTable.confirmations.removeMember
          .cancel,
      onConfirmationSuccess: () => {
        removeMember({ org_domain: org, emails: [member.email] });
      },
    });
  };

  const handleResendInvitation = (member: MemberTableItem) => {
    resendInvitation({
      input: {
        emails: [member.email],
        organization_domain: org,
        organization_role: member.role,
      },
    });
  };

  const handleRemoveInvitation = (member: MemberTableItem) => {
    const invitation = invitationsByEmail[member.email];
    if (invitation) {
      withConfirmationDs({
        title:
          copy.components.organization.memberTable.confirmations
            .removeInvitation.title,
        message: copy.components.organization.memberTable.confirmations.removeInvitation.message(
          member.email
        ),
        yesPrompt:
          copy.components.organization.memberTable.confirmations
            .removeInvitation.confirm,
        yesPromptTestId: ORGANIZATION_MEMBER_TABLE_CONFIRMATION_YES,
        noPrompt:
          copy.components.organization.memberTable.confirmations
            .removeInvitation.cancel,
        onConfirmationSuccess: () => {
          deleteInvitation({ ids: [invitation.id] });
        },
      });
    }
  };

  const searchFilter = (item: MemberTableItem, query: string): boolean => {
    const trimmed = query.trim().toLowerCase();
    return (
      item.name?.toLowerCase().includes(trimmed) ||
      item.email.toLowerCase().includes(trimmed)
    );
  };

  const renderRow = (member: MemberTableItem, idx: number) => {
    let actions: JSX.Element | null = null;
    if (
      isAdmin &&
      member.status === 'active' &&
      member.role !== OrganizationRole.OWNER &&
      member.email !== profile?.profile?.email
    ) {
      actions = (
        <MenuItem
          leftIcon={null}
          onClick={() => handleRemoveMember(member)}
          testId={ORGANIZATION_MEMBER_TABLE_ROW_ACTIONS_MENU_ITEM(
            'remove-member'
          )}
          text={copy.components.organization.memberTable.actions.removeMember}
        />
      );
    } else if (isAdmin && member.status !== 'active') {
      actions = (
        <>
          <MenuItem
            leftIcon={null}
            onClick={() => handleResendInvitation(member)}
            testId={ORGANIZATION_MEMBER_TABLE_ROW_ACTIONS_MENU_ITEM(
              'resend-invitation'
            )}
            text={
              copy.components.organization.memberTable.actions.resendInvitation
            }
          />
          <MenuItem
            leftIcon={null}
            onClick={() => handleRemoveInvitation(member)}
            testId={ORGANIZATION_MEMBER_TABLE_ROW_ACTIONS_MENU_ITEM(
              'remove-invitation'
            )}
            text={
              copy.components.organization.memberTable.actions.removeInvitation
            }
          />
        </>
      );
    }

    return (
      <>
        <td
          className="organization-table-row-user-name"
          data-testid={ORGANIZATION_MEMBER_TABLE_ROW_NAME(member.email, idx)}
        >
          <div className="organization-table-row-user-name-cell">
            {member.initials ? (
              <StaticAvatar size="md" text={member.initials} />
            ) : null}
            <div className="organization-table-row-user-name-title">
              <Body ellipsize size="md">
                {member.name || member.email}
              </Body>
              {member.name ? (
                <Body
                  className="organization-table-row-user-name-subtitle"
                  ellipsize
                  size="sm"
                >
                  {member.email}
                </Body>
              ) : null}
            </div>
          </div>
        </td>
        <td className="organization-table-row-member-role">
          <Body size="md">
            {copy.components.organization.memberTable.roles[member.role]}
          </Body>
        </td>
        {isAdmin ? (
          <td className="data-table-row-status">
            <Body
              className={`data-table-row-status--${member.status}`}
              size="md"
            >
              {copy.components.organization.memberTable.status[member.status]}
            </Body>
          </td>
        ) : null}
        {isAdmin ? (
          <td
            className="data-table-row-actions"
            data-testid={ORGANIZATION_MEMBER_TABLE_ROW_ACTIONS(
              member.email,
              idx
            )}
          >
            {actions ? (
              <Menu
                leftIcon={OverflowMenuHorizontal}
                menu={
                  <MenuItems className="data-table-menu-items">
                    {actions}
                  </MenuItems>
                }
                size="sm"
                testId={ORGANIZATION_MEMBER_TABLE_ROW_ACTIONS_MENU}
              />
            ) : null}
          </td>
        ) : null}
      </>
    );
  };

  return (
    <DataTable
      className="organization-table"
      columns={isAdmin ? ADMIN_COLUMNS : MEMBER_COLUMNS}
      data={memberItems}
      getRowKey={(member) => member.email}
      renderRow={renderRow}
      searchFilter={searchFilter}
      searchInputTestId={ORGANIZATION_MEMBER_TABLE_SEARCH}
      searchPlaceholder={copy.components.organization.memberTable.search}
      title={copy.components.organization.memberTable.title(memberItems.length)}
    />
  );
};
